import React, { useState } from 'react'
import { Button, Chip, Stack, Typography } from '@mui/material'
import { LinkOutlined, LinkOffOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { OPERATION_LINK_STATE, resolveOperationLinkState } from './operationLinkIntegrity'
import OperationUpdateBanner from './OperationUpdateBanner'
import OperationUpdateImpactDialog from './OperationUpdateImpactDialog'

/**
 * NodeOperationLinkHeader (C7, §8.7) — la cabecera de `NodeGeneralProperties` que dice de dónde
 * viene este JOB (D14, CA3.2/CA3.5) y, si su operación tiene una versión más nueva, la ofrece
 * (CA5.1, S85).
 *
 * Ajuste del PO (2026-09-15): misma estructura que la cabecera del BOM (`WorkflowBomLinkHeader`):
 * una fila de chips con la procedencia —código y versión— y, debajo, el aviso de actualización con
 * sus dos acciones. El diálogo de impacto se monta solo mientras está abierto, como allí.
 *
 * HU-07 (S116, GATE 2-bis del 16/09/2026): desde aquí se guarda en la biblioteca un job redactado
 * localmente — el MISMO acto que publicar (`intent: 'PUBLISH'`, CU-11). El botón K35 volvió el
 * 16/09 con la firma de HU-07, después de que el PO lo retirase el 15/09 mientras el circuito de Sun
 * no estaba cerrado. Solo se ofrece sobre un nodo EMBEBIDO y en edición: un nodo ya vinculado es una
 * operación de biblioteca (E-LNK-05).
 *
 * Historia 152 (S26, S27, S30, S31, S36 · RN-01.13b): la PROCEDENCIA y el ESTADO son dos cosas
 * distintas, y el nodo está en uno de los tres regímenes de `RN-G1` (más el de publicación
 * pendiente):
 * - **vinculado**: "Linked to {{code}} v{{version}}" (J14), UNA SOLA clave con interpolación — no se
 *   compone concatenando trozos traducidos, que es lo que la rompería en 7 idiomas (S42) — MÁS su
 *   estado, "Synchronized" (J18) o "Customized" (J17). El estado se pinta SOLO si hay vínculo vivo;
 * - **desvinculado**: "Unlinked from {{code}} v{{version}}" (J19) con la procedencia histórica
 *   (`originCode` / `originVersionNumber`) y SIN estado: ya no recibe avisos ni se puede proponer
 *   (RN-01.18);
 * - **embebido**: "Embedded" (J15), sin código, sin versión y sin ninguna referencia de origen. Un
 *   nodo sin vínculo nunca pinta un "Linked to …" con datos residuales (S27);
 * - **pendiente**: ni el vínculo de un nodo recién arrastrado ni el guardado en biblioteca están
 *   escritos (Δ9). Los escribe el guardado general del workflow, y hasta entonces se dicen con el
 *   chip "Pending save". La COPIA LOCAL de un job de otro proceso (Enmienda 2 del PO, 22/09/2026 ·
 *   S15) también está pendiente, pero el PO decidió (22/09/2026) que NO lleve chip: se lee como
 *   "Embedded" a secas, con sus acordeones bloqueados hasta guardar. Es **embebida** —no crea
 *   vínculo (`RN-01.8c`)— y lo que se ve de su job es una PREVISUALIZACIÓN del origen que Sun
 *   sobrescribirá al copiar.
 *
 * El paso de "Synchronized" a "Customized" se pinta EN EL MOMENTO en que el contenido diverge, antes
 * de guardar, y vuelve solo si el usuario deshace (S31, S36), con `operationLinkIntegrity.js`. Eso es
 * UX: la decisión sigue siendo del servidor (RN-01.16).
 */
const NodeOperationLinkHeader = ({
  operationLink = {},
  linkedJobReference = null,
  jobReference = null,
  workflowId = 0,
  nodeId = '',
  onUpdate = null,
  onDismiss = null,
  editing = false,
  canPublish = false,
  onPublishClick = null,
  featureGroupRevisionControlled = false,
  featureGroupBaselines = {}
}) => {
  const { t_ } = useSmartTranslation()
  const state = resolveOperationLinkState(operationLink, linkedJobReference, jobReference)
  // RN-G1: los tres regímenes son excluyentes. Solo el vinculado tiene estado que contar.
  const isUnlinked = state.state === OPERATION_LINK_STATE.UNLINKED
  const isEmbedded = state.state === OPERATION_LINK_STATE.EMBEDDED
  // Δ9: ni el vínculo de un nodo recién arrastrado ni una actualización aceptada están escritos: los
  // escribe el guardado general. Hasta entonces se ven aquí, como el BOM hace con `pendingBomLink`.
  const pendingBind = operationLink?.intent === 'BIND'
  // S73/S116: guardar en biblioteca es una intención más, y hasta el guardado general lo único que
  // hay es el código elegido.
  const pendingPublish = operationLink?.intent === 'PUBLISH'
  const pendingUpdate = operationLink?.intent === 'UPDATE'
  // Historia 152 (S47 · RN-01.17): desvincular es una ORDEN pendiente hasta el guardado. Mientras
  // tanto la cabecera sigue diciendo la verdad —el nodo AÚN está vinculado— y lo que se añade es
  // que hay algo sin guardar. La cabecera final, "Unlinked from … v…" (J19), la fija la respuesta
  // del servidor con `state`, `originCode` y `originVersionNumber` (RN-01.16).
  const pendingUnbind = operationLink?.intent === 'UNBIND'
  // Historia 152 · Enmienda 2 (PO, 22/09/2026 · S15): una COPIA LOCAL pendiente. El nodo enseña el
  // job de origen, editable desde la historia 167 (Sun lo conserva y solo trae los adjuntos al
  // guardar, `keepEditedContent`). La procedencia sigue siendo "Embedded"
  // (una copia no crea vínculo, `RN-01.8c`). Por decisión del PO (22/09/2026) NO pinta el chip
  // "Pending save": la bandera solo sirve para no ofrecer "Publish to library" sobre una
  // previsualización que Sun va a sobrescribir.
  const pendingCopy = operationLink?.intent === 'COPY'
  const pendingVersion = pendingUpdate ? (operationLink?.latestVersionNumber ?? null) : null
  // S91: la versión candidata cuyo impacto se está mirando, si el usuario pulsó "Update".
  const [updateTarget, setUpdateTarget] = useState('')

  return (
    <Stack spacing={1}>
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap data-testid='node-operation-link-header'>
        <Typography variant='caption'>{t_('Linked operation')}</Typography>

        {pendingPublish
          ? (
            <>
              <Chip
                size='small'
                color='primary'
                variant='outlined'
                icon={<LinkOutlined />}
                data-testid='node-operation-link-publishing'
                label={operationLink.publish?.code || ''}
              />
              {operationLink.publish?.name
                ? <Typography variant='body2'>{operationLink.publish.name}</Typography>
                : null}
            </>
            )
          : null}

        {!pendingPublish && state.linked
          ? (
            <>
              <Chip
                size='small'
                color='primary'
                variant='outlined'
                icon={<LinkOutlined />}
                data-testid='node-operation-link-linked'
                label={t_('Linked to {{code}} v{{version}}', {
                  code: state.code,
                  version: pendingUpdate && pendingVersion !== null ? pendingVersion : state.versionNumber
                })}
              />
              {/* Decir solo la versión destino escondería de cuál se sale, que es lo que el usuario
                  está a punto de dejar atrás — mismo criterio que la cabecera del BOM. */}
              {pendingUpdate && pendingVersion !== null
                ? (
                  <Typography variant='body2' data-testid='node-operation-link-update-from'>
                    {'(' + t_('from {{version}}', { version: state.versionNumber }) + ')'}
                  </Typography>
                  )
                : null}
              {/* S26/S30/S31: el ESTADO, que solo existe mientras el vínculo está vivo. "Customized"
                  puede venir del servidor o adelantarlo el cliente; para el usuario es lo mismo. */}
              <Chip
                size='small'
                variant='outlined'
                color={state.state === OPERATION_LINK_STATE.CUSTOMIZED ? 'warning' : 'success'}
                data-testid='node-operation-link-state'
                label={state.state === OPERATION_LINK_STATE.CUSTOMIZED ? t_('Customized') : t_('Synchronized')}
              />
            </>
            )
          : null}

        {/* S26/RN-01.18: desvinculado. La procedencia se conserva como HISTORIA —y se dice así, no
            como un vínculo— y no la acompaña ningún estado. */}
        {!pendingPublish && isUnlinked
          ? (
            <Chip
              size='small'
              variant='outlined'
              icon={<LinkOffOutlined />}
              data-testid='node-operation-link-unlinked'
              label={t_('Unlinked from {{code}} v{{version}}', {
                code: state.originCode,
                version: state.originVersionNumber
              })}
            />
            )
          : null}

        {!pendingPublish && isEmbedded
          ? (
            <Chip
              size='small'
              variant='outlined'
              icon={<LinkOffOutlined />}
              data-testid='node-operation-link-embedded'
              label={t_('Embedded')}
            />
            )
          : null}

        {pendingBind || pendingUpdate || pendingPublish || pendingUnbind
          ? <Chip size='small' color='warning' label={t_('Pending save')} data-testid='node-operation-link-pending-save' />
          : null}

        {/* S73/S74/S76/S116: guardar en la biblioteca solo se ofrece sobre un nodo EMBEBIDO, en
            edición y con el permiso de escritura. K35 sigue siendo el literal congelado de §8.8: el
            "Save to library" del Gherkin de HU-07 nombra el acto, no la clave. */}
        {canPublish && editing && isEmbedded && !pendingPublish && !pendingCopy
          ? (
            <Button size='small' variant='outlined' data-testid='node-publish-operation' onClick={onPublishClick}>
              {t_('Publish to library')}
            </Button>
            )
          : null}
      </Stack>

      {/* S85/S105: el aviso vive bajo la procedencia, igual que en la sección del BOM. Un nodo cuyo
          vínculo acaba de romperse en pantalla no avisa de nada: ya no está vinculado. Y con una
          actualización ya aceptada tampoco: lo que falta es guardar, no volver a decidir. */}
      {state.linked && !pendingUpdate && !pendingPublish && !pendingUnbind
        ? (
          <OperationUpdateBanner
            operationLink={operationLink}
            onUpdate={(update) => setUpdateTarget(update.nodeId)}
            onDismiss={(versionNumber) => onDismiss?.(versionNumber)}
          />
          )
        : null}

      {/* S91: el impacto se monta solo mientras está abierto, así cada apertura parte de cero. */}
      {updateTarget
        ? (
          <OperationUpdateImpactDialog
            open
            workflowId={workflowId}
            nodeId={nodeId}
            targetNodeId={updateTarget}
            featureGroupRevisionControlled={featureGroupRevisionControlled}
            jobReference={jobReference}
            featureGroupBaselines={featureGroupBaselines}
            onConfirm={(targetNodeId) => {
              setUpdateTarget('')
              onUpdate?.(targetNodeId)
            }}
            onClose={() => setUpdateTarget('')}
          />
          )
        : null}
    </Stack>
  )
}

export default NodeOperationLinkHeader
